import { Directive, ElementRef, OnDestroy, Renderer2, AfterViewInit } from '@angular/core';

@Directive({
    selector: '[appAppendToBody]',
    standalone: false
})
export class AppendToBodyDirective implements AfterViewInit, OnDestroy {
  private dropdownMenu: HTMLElement | null = null;
  private mutationObserver: MutationObserver | null = null;
  private originalParent: HTMLElement | null = null;
  private placeholder: Comment | null = null;
  private button: HTMLElement | null = null;
  private isAppendedToBody = false;
  private clickListener: (() => void) | null = null;

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2
  ) {}

  ngAfterViewInit(): void {
    // Find the dropdown menu within this dropdown container
    const dropdownElement = this.elementRef.nativeElement as HTMLElement;
    this.dropdownMenu = dropdownElement.querySelector('.dropdown-menu');
    this.button = dropdownElement.querySelector('[data-bs-toggle="dropdown"]');

    if (!this.dropdownMenu) {
      console.warn('AppendToBodyDirective: No .dropdown-menu found');
      return;
    }

    // Set fixed width for dropdown menu
    this.renderer.setStyle(this.dropdownMenu, 'min-width', '160px');
    this.renderer.setStyle(this.dropdownMenu, 'max-width', '200px');

    // Store the original parent
    this.originalParent = this.dropdownMenu.parentElement;

    // Create a placeholder comment node to mark the original position
    this.placeholder = document.createComment('dropdown-menu-placeholder');
    this.renderer.insertBefore(
      this.originalParent,
      this.placeholder,
      this.dropdownMenu
    );

    // Watch for the 'show' class to be added/removed
    this.mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const target = mutation.target as HTMLElement;
          if (target.classList.contains('show')) {
            this.appendToBody();
          } else {
            this.returnToOriginalParent();
          }
        }
      });
    });

    // Start observing the dropdown menu for class changes
    this.mutationObserver.observe(this.dropdownMenu, {
      attributes: true,
      attributeFilter: ['class']
    });

    // Listen for clicks outside to close
    this.setupOutsideClickListener();
  }

  private setupOutsideClickListener(): void {
    this.clickListener = this.renderer.listen('document', 'click', (event: MouseEvent) => {
      if (!this.isAppendedToBody || !this.dropdownMenu) return;

      const target = event.target as HTMLElement;
      const clickedInside = this.dropdownMenu.contains(target) ||
                            (this.button && this.button.contains(target));

      if (!clickedInside && this.dropdownMenu.classList.contains('show')) {
        // Remove the show class to trigger Bootstrap's hide
        this.dropdownMenu.classList.remove('show');
        this.returnToOriginalParent();
      }
    });
  }

  private appendToBody(): void {
    if (!this.dropdownMenu || !this.button) return;

    // Close any other open dropdowns first
    this.closeOtherDropdowns();

    // Calculate position relative to viewport
    const buttonRect = this.button.getBoundingClientRect();

    // Get dropdown menu dimensions
    const menuWidth = 160; // Fixed width
    const menuHeight = this.dropdownMenu.offsetHeight || 200; // Approximate height

    // Calculate position (align to right of button)
    let top = buttonRect.bottom + 2;
    let left = buttonRect.right - menuWidth;

    // Check if dropdown would go off screen
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // Adjust if going off bottom of screen
    if (top + menuHeight > viewportHeight) {
      top = buttonRect.top - menuHeight - 2;
    }

    // Adjust if going off left of screen
    if (left < 0) {
      left = buttonRect.left;
    }

    // Adjust if going off right of screen
    if (left + menuWidth > viewportWidth) {
      left = viewportWidth - menuWidth - 10;
    }

    // Append to body
    document.body.appendChild(this.dropdownMenu);
    this.isAppendedToBody = true;

    // Position the dropdown menu
    this.renderer.setStyle(this.dropdownMenu, 'position', 'fixed');
    this.renderer.setStyle(this.dropdownMenu, 'z-index', '99999');
    this.renderer.setStyle(this.dropdownMenu, 'top', `${top}px`);
    this.renderer.setStyle(this.dropdownMenu, 'left', `${left}px`);
    this.renderer.setStyle(this.dropdownMenu, 'width', `${menuWidth}px`);
    this.renderer.setStyle(this.dropdownMenu, 'transform', 'none');
    this.renderer.setStyle(this.dropdownMenu, 'margin', '0');
  }

  private closeOtherDropdowns(): void {
    // Find all dropdown menus that are currently appended to body
    const bodyDropdowns = document.body.querySelectorAll('.dropdown-menu.show');
    bodyDropdowns.forEach((dropdown: Element) => {
      if (dropdown !== this.dropdownMenu) {
        dropdown.classList.remove('show');
      }
    });
  }

  private returnToOriginalParent(): void {
    if (!this.dropdownMenu || !this.originalParent || !this.placeholder) return;

    // Only return if it's actually in the body
    if (this.dropdownMenu.parentElement !== document.body) return;

    // Return the dropdown menu to its original position
    this.renderer.insertBefore(
      this.originalParent,
      this.dropdownMenu,
      this.placeholder.nextSibling
    );

    this.isAppendedToBody = false;

    // Remove inline styles
    this.renderer.removeStyle(this.dropdownMenu, 'position');
    this.renderer.removeStyle(this.dropdownMenu, 'top');
    this.renderer.removeStyle(this.dropdownMenu, 'left');
    this.renderer.removeStyle(this.dropdownMenu, 'width');
    this.renderer.removeStyle(this.dropdownMenu, 'transform');
    this.renderer.removeStyle(this.dropdownMenu, 'margin');
  }

  ngOnDestroy(): void {
    // Clean up
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }

    // Remove click listener
    if (this.clickListener) {
      this.clickListener();
    }

    // Return dropdown to original position if it's still in body
    if (this.dropdownMenu && this.dropdownMenu.parentElement === document.body) {
      this.returnToOriginalParent();
    }

    // Remove placeholder
    if (this.placeholder && this.placeholder.parentNode) {
      this.placeholder.parentNode.removeChild(this.placeholder);
    }
  }
}
